import { Pause, Play } from 'lucide-react'
import { useWsStatus } from '@/hooks/useWebSocket'
import { cn } from '@/lib/cn'

interface Props {
  paused: boolean
  bufferedCount: number
  onTogglePause: () => void
}

/**
 * Header live state. Three visual modes:
 *   - Live (green pulsing dot) + "Pause" button
 *   - Paused (amber static dot) + "Resume (n buffered)" button
 *   - Reconnecting (amber pulsing dot)
 *   - Offline (grey static dot)
 */
export function LiveIndicator({ paused, bufferedCount, onTogglePause }: Props) {
  const ws = useWsStatus()

  let dotClass = 'bg-muted-fg'
  let labelText: string = 'Offline'
  let labelTone = 'text-muted-fg'
  let animate = false

  if (paused) {
    dotClass = 'bg-status-pending'
    labelText = bufferedCount > 0 ? `Paused · ${bufferedCount} buffered` : 'Paused'
    labelTone = 'text-status-pending'
  } else if (ws === 'open') {
    dotClass = 'bg-status-success'
    labelText = 'Live'
    labelTone = 'text-status-success'
    animate = true
  } else if (ws === 'connecting') {
    dotClass = 'bg-status-pending'
    labelText = 'Reconnecting…'
    labelTone = 'text-status-pending'
    animate = true
  }

  return (
    <div className="flex items-center gap-2 text-xs">
      <span
        className={cn('inline-block h-2 w-2 rounded-full', dotClass, animate && 'live-dot')}
        aria-hidden
      />
      <span className={labelTone}>{labelText}</span>
      <button
        type="button"
        onClick={onTogglePause}
        aria-label={paused ? 'Resume real-time updates' : 'Pause real-time updates'}
        className="rounded p-1 text-muted-fg hover:bg-muted hover:text-fg"
      >
        {paused ? <Play size={12} /> : <Pause size={12} />}
      </button>
    </div>
  )
}
